import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { Dispatch } from 'redux';
import { useHistory } from 'react-router-dom';
// import { Animated } from 'react-animated-css'; // Removed due to deprecated lifecycle methods
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatEther } from '@ethersproject/units';
import { Web3Provider } from '@ethersproject/providers';
import { useWeb3React } from '@web3-react/core';
import {
  AllowedNetworks,
  NetworkChainId,
  useMetamaskEagerConnect,
  useMetamaskListener,
} from './web3Utils';
import { useWeb3ReactLegacy, getWeb3Provider } from '../../utils/web3-compat';
import { useInjectedWalletIfAvailable } from '../../connectors/InjectedWalletContext';
import { Button } from '../../pulsechain_components/Button';
import { WorkflowPageTemplate } from '../../pulsechain_components/WorkflowPage/WorkflowPageTemplate';
import { DepositKeyInterface, StoreState } from '../../store/reducers';
import { routesEnum } from '../../Routes';
import { Link } from '../../pulsechain_components/Link';
import { Text } from '../../pulsechain_components/Text';
import { Paper } from '../../pulsechain_components/Paper';
import { Heading } from '../../pulsechain_components/Heading';
import { Dot } from '../../pulsechain_components/Dot';
import { numberWithCommas } from '../../utils/numberWithCommas';
import {
  DispatchWorkflowUpdateType,
  updateWorkflow,
  WorkflowStep,
} from '../../store/actions/workflowActions';
import { DepositStatus } from '../../store/actions/depositFileActions';
import {
  IS_MAINNET,
  PRICE_PER_VALIDATOR,
  TICKER_NAME,
  FAUCET_URL,
} from '../../utils/envVars';
import { routeToCorrectWorkflowStep } from '../../utils/RouteToCorrectWorkflowStep';
import useIntlNetworkName from '../../hooks/useIntlNetworkName';
import MultiWalletButton from './MultiWalletButton';

// styled components
const Container = styled.div`
  margin: auto;
  position: relative;
  margin-bottom: 40px;
  margin-top: 40px;
  @media only screen and (max-width: ${p => p.theme.screenSizes.large}) {
    margin-bottom: 0;
  }
`;
const WalletConnectedContainer = styled.div`
  pointer-events: none;
  width: 800px;
  margin: auto;
  position: absolute;
  left: calc(50% - 400px); // center - half width
  @media only screen and (max-width: ${p => p.theme.screenSizes.large}) {
    width: 100%;
    left: 0;
    height: 100%;
  }
`;

const WalletButtonContainer = styled.div`
  margin: auto;
`;
const WalletButtonSubContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 750px;
  margin: auto;
  @media only screen and (max-width: ${p => p.theme.screenSizes.large}) {
    width: 400px;
  }
`;

const FadeContainer = styled.div<{
  isVisible: boolean;
  animateRight?: boolean;
}>`
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transform: ${props =>
    props.animateRight
      ? `translateX(${props.isVisible ? '0' : '20px'})`
      : `translateX(${props.isVisible ? '0' : '-20px'})`};
  transition: opacity 200ms ease-in-out, transform 200ms ease-in-out;
  pointer-events: ${props => (props.isVisible ? 'auto' : 'none')};
`;
const WalletInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${(p: { theme: any }) => p.theme.gray.medium};
  padding-bottom: 20px;
`;
const StatusText = styled((Text as any) as any)`
  font-size: 18px;
`;
const FaucetLink = styled(Link as any)`
  margin: auto;
  margin-top: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const Network = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
`;

const Balance = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 16px;
  margin-top: 24px;
`;

const ButtonRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-top: 428px;
  @media only screen and (max-width: ${p => p.theme.screenSizes.large}) {
    flex-direction: column;
    align-items: center;
  }
`;

const MetaMaskError = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 0px;
`;

// Prop definitions
interface OwnProps {}
interface StateProps {
  workflow: WorkflowStep;
  depositKeys: DepositKeyInterface[];
}
interface DispatchProps {
  dispatchWorkflowUpdate: DispatchWorkflowUpdateType;
}
type Props = StateProps & DispatchProps & OwnProps;

const _ConnectWalletPage = ({
  workflow,
  dispatchWorkflowUpdate,
  depositKeys,
}: Props): JSX.Element => {
  const history = useHistory();
  // get wallet info from Web3React compatibility layer
  const {
    active: walletConnected,
    deactivate,
    chainId,
    connector: walletProvider,
    error,
    account,
  } = useWeb3ReactLegacy();

  // Get the injected wallet context (for Rabby, Brave, etc.)
  const injectedWallet = useInjectedWalletIfAvailable();

  // Get library provider - prioritize injected wallet to avoid postMessage cloning error
  // (GitLab issue #4: Rabby/Brave wallets fail when using web3-react's provider)
  const { provider: web3ReactProvider } = useWeb3React();
  const injectedProvider = injectedWallet?.getProvider?.();
  const rawProvider = getWeb3Provider({
    injectedProvider,
    web3ReactProvider,
  });

  // Only create Web3Provider when wallet is actually connected
  // Creating it too early triggers network detection RPC calls that can fail
  const library = useMemo(() => {
    if (!walletConnected || !account || !rawProvider) {
      return undefined;
    }
    try {
      return new Web3Provider(rawProvider as any);
    } catch {
      return undefined;
    }
  }, [walletConnected, account, rawProvider]);
  const { executionLayerName } = useIntlNetworkName();

  // initialize state
  const [balance, setBalance] = useState<number | null>(null);
  const [lowBalance, setLowBalance] = useState<boolean>(false);
  const [network, setNetwork] = useState<string>('');
  const [networkAllowed, setNetworkAllowed] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('');
  const isInvalidNetwork = useMemo(() => {
    return (
      error &&
      (error.message?.includes('Unsupported chain') ||
        error.message?.includes('wrong network') ||
        error.message === 'Invariant failed: chainId 0xNaN is not an integer')
    );
  }, [error]);
  const balanceRef = useRef<number | null>(null);
  const { formatMessage } = useIntl();
  const remainingDepositCount = useMemo(
    () =>
      depositKeys.filter(
        key => key.depositStatus !== DepositStatus.ALREADY_DEPOSITED
      ).length,
    [depositKeys]
  );

  // sets balanceRef to always have current balance (to refer to in callbacks)
  balanceRef.current = balance;

  // setup RPC event listener
  const attemptedMMConnection: boolean = useMetamaskEagerConnect();
  useMetamaskListener(!attemptedMMConnection);

  // sets the balance to the current wallet on provider or network change
  useEffect((): any => {
    if (!!account && !!library) {
      library
        .getBalance(account)
        .then((amount: any) => {
          const formattedBalance = Number(
            parseFloat(formatEther(amount)).toPrecision(5)
          );
          // @ts-ignore (type check performed in envVars.ts)
          const requiredBalance = remainingDepositCount * PRICE_PER_VALIDATOR;

          setBalance(formattedBalance);
          setLowBalance(formattedBalance < requiredBalance);
        })
        .catch(() => setBalance(null));
      return () => setBalance(null);
    }
  }, [walletProvider, library, chainId, depositKeys, account]);

  // adds event emitter to listen to new blocks & update balance if it changed
  useEffect((): any => {
    if (!!account && !!library) {
      const lib = library; // Capture for closure
      lib.on('block', () => {
        lib
          .getBalance(account)
          .then((amount: any) => {
            const formattedBalance = Number(
              parseFloat(formatEther(amount)).toPrecision(5)
            );
            if (formattedBalance !== balanceRef.current) {
              setBalance(formattedBalance);
              // @ts-ignore (type check performed in envVars.ts)
              const requiredBalance =
                remainingDepositCount * PRICE_PER_VALIDATOR;
              setLowBalance(formattedBalance < requiredBalance);
            }
          })
          .catch(() => setBalance(null));
      });

      return () => lib.off('block');
    }
  }, [library, account, depositKeys]);

  const getWalletName = useCallback((): string => {
    // Get wallet name from injected wallet context (handles Rabby, Brave, MetaMask, etc.)
    const walletType = injectedWallet?.walletType;
    if (walletType) {
      // Convert wallet type enum to display name
      switch (walletType) {
        case 'metamask':
          return 'MetaMask';
        case 'rabby':
          return 'Rabby';
        case 'brave':
          return 'Brave Wallet';
        case 'trust':
          return 'Trust Wallet';
        case 'coinbase':
          return 'Coinbase Wallet';
        case 'phantom':
          return 'Phantom';
        case 'tokenpocket':
          return 'TokenPocket';
        default:
          return 'Wallet';
      }
    }
    return 'Wallet';
  }, [injectedWallet?.walletType]);

  // sets the status copy on provider or network change
  useEffect(() => {
    if (chainId) {
      setNetwork(NetworkChainId[chainId]);
      setNetworkAllowed(Object.values(AllowedNetworks).includes(network));
    }

    if (
      walletConnected &&
      networkAllowed &&
      !error &&
      (balance || balance === 0)
    ) {
      setStatus(`${numberWithCommas(balance)} ${TICKER_NAME}`);
    } else if (walletConnected && error) {
      setStatus(formatMessage({ defaultMessage: 'Error' }));
    } else if (!networkAllowed) {
      setStatus(
        formatMessage(
          { defaultMessage: 'Connect {wallet} to {executionLayerName}' },
          {
            wallet: getWalletName(),
            executionLayerName,
          }
        )
      );
    }
  }, [
    chainId,
    walletConnected,
    networkAllowed,
    error,
    balance,
    network,
    formatMessage,
    walletProvider,
    executionLayerName,
    getWalletName,
  ]);

  const handleSubmit = () => {
    // Allow submit if at this step or if user came back from a later step
    if (workflow >= WorkflowStep.CONNECT_WALLET) {
      dispatchWorkflowUpdate(WorkflowStep.SUMMARY);
      // Navigate after state update
      setTimeout(() => {
        history.push(routesEnum.summaryPage);
      }, 100);
    }
  };

  // Redirect if workflow step is too early
  if (workflow < WorkflowStep.CONNECT_WALLET) {
    return routeToCorrectWorkflowStep(workflow);
  }

  // Redirect if no deposit keys (user may have stale persisted state)
  if (depositKeys.length === 0) {
    return routeToCorrectWorkflowStep(WorkflowStep.UPLOAD_VALIDATOR_FILE);
  }

  return (
    <WorkflowPageTemplate
      title={formatMessage({ defaultMessage: 'Connect wallet' })}
    >
      <Container>
        {walletConnected && (
          <WalletConnectedContainer>
            <FadeContainer isVisible={walletConnected} animateRight={true}>
              <Paper pad="medium">
                <WalletInfoContainer>
                  <Heading level={3} size="small" color="greenLight">
                    {getWalletName()}
                  </Heading>
                  {account && (
                    <Text size="medium">
                      {`${account.slice(0, 6)}...${account.slice(-6)}`}
                    </Text>
                  )}
                </WalletInfoContainer>
                <Network>
                  <Row>
                    <Dot
                      className="mr10"
                      success={networkAllowed}
                      error={!networkAllowed}
                    />
                    <Heading
                      level={3}
                      size="small"
                      color="greenLight"
                      className="mt0"
                    >
                      <FormattedMessage defaultMessage="Network" />
                    </Heading>
                  </Row>
                  <Text color={networkAllowed ? 'greenDark' : 'redMedium'}>
                    {executionLayerName}
                  </Text>
                </Network>
                <div>
                  {!networkAllowed && (
                    <>
                      <StatusText className="mt10">{status}</StatusText>
                    </>
                  )}
                </div>
                {networkAllowed && (
                  <>
                    <Balance>
                      <Row>
                        <Dot
                          className="mr10"
                          success={!lowBalance}
                          error={lowBalance}
                        />
                        <Heading level={3} size="small" color="greenLight">
                          <FormattedMessage defaultMessage="Balance" />
                        </Heading>
                      </Row>
                      <StatusText className="mt10">{status}</StatusText>
                    </Balance>
                    <div>
                      {networkAllowed && lowBalance && (
                        <>
                          <FormattedMessage
                            defaultMessage="You do not have enough {eth} in this account for
                          {numberOfValidators} {validator}"
                            values={{
                              numberOfValidators: remainingDepositCount,
                              eth: TICKER_NAME,
                              validator:
                                remainingDepositCount > 1
                                  ? formatMessage({
                                      defaultMessage: 'validators',
                                    })
                                  : formatMessage({
                                      defaultMessage: 'validator',
                                    }),
                            }}
                          />
                        </>
                      )}
                      {!IS_MAINNET && lowBalance && (
                        <FaucetLink shouldOpenNewTab to={FAUCET_URL} primary>
                          <FormattedMessage
                            defaultMessage="Get {TICKER_NAME}"
                            values={{
                              TICKER_NAME,
                            }}
                          />
                        </FaucetLink>
                      )}
                    </div>
                  </>
                )}
              </Paper>
            </FadeContainer>
          </WalletConnectedContainer>
        )}
        {!walletConnected && (
          <WalletButtonContainer>
            <FadeContainer isVisible={!walletConnected} animateRight={false}>
              <WalletButtonSubContainer>
                <MultiWalletButton />
              </WalletButtonSubContainer>
            </FadeContainer>
          </WalletButtonContainer>
        )}
      </Container>

      {error && error.message?.includes('No Ethereum provider') && (
        <MetaMaskError>
          <Text className="mb30">
            <FormattedMessage defaultMessage="We can't detect MetaMask. Switch browsers or install MetaMask." />
          </Text>
          <Link isTextLink={false} to="https://metamask.io/">
            <Button
              className="mr10"
              label={formatMessage({ defaultMessage: 'Download MetaMask' })}
            />
          </Link>
        </MetaMaskError>
      )}

      {isInvalidNetwork && (
        <div className="flex center mt20">
          <FormattedMessage
            defaultMessage="Your wallet is on the wrong network. Switch to {executionLayerName}"
            values={{ executionLayerName }}
          />
        </div>
      )}

      <ButtonRow>
        {!walletConnected && (
          <Link to={routesEnum.uploadValidatorPage}>
            <Button
              className="mr10"
              width={100}
              label={formatMessage({ defaultMessage: 'Back' })}
            />
          </Link>
        )}
        {walletConnected && (
          <Button
            width={300}
            onClick={deactivate}
            label={formatMessage({ defaultMessage: 'Connect new wallet' })}
            className="mr10"
            color="greenLight"
          />
        )}
        <Button
          width={300}
          disabled={
            !walletProvider || !walletConnected || !networkAllowed || lowBalance
          }
          label={formatMessage({ defaultMessage: 'Continue' })}
          onClick={() => {
            handleSubmit();
          }}
        />
      </ButtonRow>
    </WorkflowPageTemplate>
  );
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  dispatchWorkflowUpdate: step => dispatch(updateWorkflow(step)),
});

const mapStateToProps = ({
  workflow,
  depositFile,
}: StoreState): StateProps => ({
  workflow,
  depositKeys: depositFile.keys,
});

export const ConnectWalletPage = connect<
  StateProps,
  DispatchProps,
  OwnProps,
  StoreState
>(
  mapStateToProps,
  mapDispatchToProps
)(_ConnectWalletPage);
